import { localeCodes, locales } from "../i18n/locales.js";
import { buildAlternates } from "./alternates.js";
import { absoluteUrl, DEFAULT_OG_IMAGE, localeLabels, SITE_NAME } from "./metaDefaults.js";
import { getRouteRecord } from "./routeRecords.js";
import { buildJsonLd } from "./schema.js";

const localeByCode = Object.fromEntries(locales.map((locale) => [locale.code, locale]));

function titleFor(record) {
  if (record.entity?.type === "article") {
    return `${record.title} | ${SITE_NAME}`;
  }

  const language = localeLabels[record.lang] || record.lang;
  return `${record.title} | ${record.sectionLabel} | ${language} | ${SITE_NAME}`;
}

function imageFor(record) {
  if (record.image) return absoluteUrl(record.image);
  const sectionImage = `/assets/og/${record.sectionKey}.svg`;
  return absoluteUrl(sectionImage || DEFAULT_OG_IMAGE);
}

function ogLocaleFor(lang) {
  return localeByCode[lang]?.ogLocale || lang;
}

function normalizePathname(pathname = "/") {
  const value = String(pathname || "/");
  const path = value.startsWith("/") ? value : `/${value}`;
  return path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;
}

function notFoundRecord(pathname) {
  const path = normalizePathname(pathname);
  const [, requestedLocale] = path.match(/^\/([^/]+)/) || [];
  const lang = localeCodes.includes(requestedLocale) ? requestedLocale : "en";

  return {
    id: `not-found:${path}`,
    lang,
    path,
    sectionKey: "home",
    sectionLabel: "Not found",
    title: "Page not found",
    description: "This page is not available on the public site.",
    entity: null,
    robots: "noindex,follow",
    alternates: [],
    ogAlternateLocales: []
  };
}

export function buildMeta(pathname) {
  const record = getRouteRecord(pathname);

  if (!record) {
    return buildMetaFromRecord(notFoundRecord(pathname));
  }

  return buildMetaFromRecord(record);
}

export function buildMetaFromRecord(record) {
  const canonical = absoluteUrl(record.path);
  const meta = {
    id: record.id,
    lang: record.lang,
    path: record.path,
    title: titleFor(record),
    description: record.description,
    canonical,
    image: imageFor(record),
    alternates: record.alternates || buildAlternates(record),
    ogLocale: ogLocaleFor(record.lang),
    ogAlternateLocales: record.ogAlternateLocales || localeCodes.filter((lang) => lang !== record.lang).map(ogLocaleFor),
    ogType: record.entity?.type === "article" ? "article" : "website",
    sectionKey: record.sectionKey,
    robots: record.robots || "index,follow",
    author: record.author || null,
    datePublished: record.datePublished || null,
    dateModified: record.dateModified || record.datePublished || null,
    articleSection: record.articleSection || record.sectionLabel,
    tags: record.tags || [],
    imageAlt: record.imageAlt || null,
    rssFeed: absoluteUrl("/tgwe-rssfeed.xml")
  };

  meta.jsonLd = buildJsonLd(record, meta);
  return meta;
}

export function requiredHreflangs() {
  return [...localeCodes, "x-default"];
}
